// src/hooks/useSpeechInput.js
// Speech-to-text hook — manual mode
// User bolta hai → text input mein fill hota hai → user button dabata hai
// Language auto-detect: input khali = en-IN, Hindi text hai = hi-IN

import { useState, useRef, useEffect, useCallback } from "react";

export default function useSpeechInput(onTranscript) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError]             = useState(null);

  const recognitionRef   = useRef(null);
  const stoppedManually  = useRef(false);
  const onTranscriptRef  = useRef(onTranscript);

  // Keep onTranscript ref fresh without triggering re-renders
  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  // ── Check browser support once ────────────────────────────────
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) setIsSupported(true);
  }, []);

  // ── Cleanup on unmount ────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // ── Start listening ───────────────────────────────────────────
  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    // Stop any existing session first
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    setError(null);
    stoppedManually.current = false;

    const recognition = new SR();
    recognitionRef.current = recognition;

    // ── Auto language detect ──────────────────────────────────
    // Agar input mein already Hindi (Devanagari) text hai → hi-IN
    // Warna → en-IN (Indian English)
    const inputEl     = document.querySelector('input[type="text"]');
    const currentText = inputEl?.value || "";
    const hasHindi    = /[\u0900-\u097F]/.test(currentText);
    recognition.lang  = hasHindi ? "hi-IN" : "en-IN";

    recognition.continuous      = false; // single utterance — cleaner on mobile
    recognition.interimResults  = true;  // live text as user speaks
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    // Live transcript — fills input as user speaks
    recognition.onresult = (event) => {
      let interimText = "";
      let finalText   = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += transcript;
        } else {
          interimText += transcript;
        }
      }

      const text = finalText || interimText;
      if (text && onTranscriptRef.current) {
        onTranscriptRef.current(text.trim());
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      recognitionRef.current = null;

      if (event.error === "aborted") return; // manual stop — ignore

      if (event.error === "not-allowed") {
        setError("mic-denied");
      } else if (event.error === "no-speech") {
        setError("no-speech");
        setTimeout(() => setError(null), 2000);
      } else {
        setError("error");
        setTimeout(() => setError(null), 2000);
      }
    };

    try {
      recognition.start();
    } catch (e) {
      setIsListening(false);
    }
  }, []); // ← empty dependency array — language ab dynamic hai, onTranscript ref se handle

  // ── Stop listening ────────────────────────────────────────────
  const stopListening = useCallback(() => {
    stoppedManually.current = true;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  // ── Toggle ────────────────────────────────────────────────────
  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    isListening,
    isSupported,
    error,
    startListening,
    stopListening,
    toggleListening,
  };
}